import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  RefreshCw, ExternalLink, Code2, Eye, X, Folder, File as FileIcon,
  ChevronRight, ChevronDown, Loader2, AlertCircle, CheckCircle2, LayoutDashboard,
  GitBranch, BookMarked, UploadCloud,
} from 'lucide-react';
import DashboardShell from './dashboard/DashboardShell';

const API = process.env.REACT_APP_BACKEND_URL || '';

const GithubIcon = ({ size = 16, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style} aria-hidden="true">
    <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.38 7.86 10.9.58.1.79-.25.79-.55v-2.1c-3.2.69-3.87-1.36-3.87-1.36-.52-1.33-1.28-1.68-1.28-1.68-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.7 1.25 3.36.95.1-.74.4-1.25.73-1.54-2.55-.29-5.24-1.28-5.24-5.68 0-1.25.45-2.28 1.18-3.08-.12-.29-.51-1.46.11-3.05 0 0 .96-.31 3.15 1.18.91-.25 1.89-.38 2.87-.38s1.96.13 2.87.38c2.19-1.49 3.15-1.18 3.15-1.18.62 1.59.23 2.76.11 3.05.73.8 1.18 1.83 1.18 3.08 0 4.41-2.69 5.39-5.25 5.67.41.36.78 1.07.78 2.15v3.19c0 .3.21.66.79.55A11.5 11.5 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5z"/>
  </svg>
);

const STATUS_LABEL = {
  cloned: { label: 'Cloned', color: '#3B82F6', pulse: false },
  installing: { label: 'Installing', color: '#FFB020', pulse: true },
  building: { label: 'Building', color: '#FFB020', pulse: true },
  ready: { label: 'Ready', color: '#7C3AED', pulse: false },
  build_failed: { label: 'Build failed', color: '#EF4444', pulse: false },
  build_timeout: { label: 'Timeout', color: '#EF4444', pulse: false },
};

export default function ImportedProjectView({ project, onClose, onRefresh, embedded = false, chatId = null }) {
  const [proj, setProj] = useState(project);
  const [viewMode, setViewMode] = useState('preview'); // preview | code | dashboard
  const [iframeKey, setIframeKey] = useState(0);
  const [files, setFiles] = useState([]);
  const [openFile, setOpenFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [loadingFile, setLoadingFile] = useState(false);
  const [githubConnected, setGithubConnected] = useState(null); // null=checking
  const [reconnecting, setReconnecting] = useState(false);
  // Save to GitHub modal — Emergent-style picker (Account → Repo → Branch).
  // The commit message is auto-generated server-side from the diff stat so
  // the founder taps Save and is done (Vercel/Lovable-grade UX).
  const [pushOpen, setPushOpen] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);   // {ok, commit_sha, repo_url, ...} | {error}
  const [ghAccount, setGhAccount] = useState(null);     // {login, avatar_url}
  const [accountOpen, setAccountOpen] = useState(false); // dropdown — switch GitHub owner
  const [ghRepos, setGhRepos] = useState([]);           // [{full_name, owner_login, default_branch, ...}]
  const [ghReposLoading, setGhReposLoading] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState(null);  // full_name string
  const [repoOpen, setRepoOpen] = useState(false);      // dropdown open
  const [ghBranches, setGhBranches] = useState([]);     // [{name, default}]
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState('main');
  const [branchOpen, setBranchOpen] = useState(false);
  // Inline "+ Create New Branch" — opens an input row in the Branch dropdown.
  const [newBranchOpen, setNewBranchOpen] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [creatingBranch, setCreatingBranch] = useState(false);
  const pollRef = useRef(null);

  // When Aria's edit lands on this project, the backend rebuilds the SPA
  // bundle. We refresh the iframe IMMEDIATELY so the user sees the
  // "Rebuilding preview…" placeholder (the serve endpoint auto-detects
  // mid-rebuild and returns a spinner that meta-refreshes every 4s on its
  // own — no white page, no JSON 404). Then we poll the project status
  // and do ONE final refresh as soon as it flips back to `ready` AND the
  // entry file is rewritten, so the user sees the new build the instant
  // it lands.
  useEffect(() => {
    const handler = (ev) => {
      const edit = ev?.detail?.edit;
      if (!edit || edit.project_id !== proj?.project_id) return;
      setIframeKey(k => k + 1);

      let cancelled = false;
      let attempts = 0;
      const pollAndRefresh = async () => {
        // Up to ~90s (45 attempts × 2s) — covers slow CRA builds on big repos
        while (!cancelled && attempts < 45) {
          attempts += 1;
          await new Promise(r => setTimeout(r, 2000));
          try {
            const r = await fetch(`${API}/api/projects/${proj.project_id}/details`, { credentials: 'include' });
            if (!r.ok) continue;
            const data = await r.json();
            const st = data?.project?.status;
            // Status flipped back to ready AND the project record was
            // updated AFTER the rebuild started → the new build is live.
            if (st === 'ready') {
              if (cancelled) return;
              setIframeKey(k => k + 1);
              return;
            }
          } catch (_) { /* keep polling */ }
        }
      };
      pollAndRefresh();
      return () => { cancelled = true; };
    };
    window.addEventListener('aria-edit-applied', handler);
    return () => window.removeEventListener('aria-edit-applied', handler);
  }, [proj?.project_id, proj?.type]);

  // Check GitHub connection status on mount + when window regains focus
  const checkGithub = useCallback(async () => {
    try {
      // Use /api/github/repos to detect orphan installations (it cleans DB on 409)
      const r = await fetch(`${API}/api/github/repos`, { credentials: 'include' });
      if (r.ok) {
        setGithubConnected(true);
      } else if (r.status === 409 || r.status === 400) {
        setGithubConnected(false);
      } else {
        // Fall back to status check
        const sr = await fetch(`${API}/api/github/status`, { credentials: 'include' });
        if (sr.ok) {
          const sd = await sr.json();
          setGithubConnected(!!sd.connected);
        } else {
          setGithubConnected(false);
        }
      }
    } catch {
      setGithubConnected(false);
    }
  }, []);

  useEffect(() => { checkGithub(); }, [checkGithub]);

  useEffect(() => {
    const onFocus = () => checkGithub();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [checkGithub]);

  const handleReconnect = async () => {
    setReconnecting(true);
    try {
      const r = await fetch(`${API}/api/github/connect`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to start GitHub connect');
      const d = await r.json();
      window.location.href = d.redirect_url;
    } catch {
      setReconnecting(false);
    }
  };

  // Poll status while building
  useEffect(() => {
    const needsPoll = proj?.status && ['cloned', 'installing', 'building'].includes(proj.status);
    if (!needsPoll) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/import/${proj.project_id}/status`, { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          setProj(d);
          if (['ready', 'build_failed', 'build_timeout'].includes(d.status)) {
            clearInterval(pollRef.current);
            setIframeKey(k => k + 1); // reload iframe when ready
          }
        }
      } catch {}
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [proj?.status, proj?.project_id]);

  // Load file tree
  const loadFiles = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/import/${proj.project_id}/tree`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setFiles(d.files || []);
      }
    } catch {}
  }, [proj?.project_id]);

  useEffect(() => {
    if (viewMode === 'code' && files.length === 0) loadFiles();
  }, [viewMode, files.length, loadFiles]);

  const loadFileContent = async (path) => {
    setLoadingFile(true);
    setOpenFile(path);
    try {
      const r = await fetch(`${API}/api/import/${proj.project_id}/file/${path}`, { credentials: 'include' });
      if (r.ok) {
        const txt = await r.text();
        setFileContent(txt);
      } else {
        setFileContent(`// Unable to load ${path}`);
      }
    } catch (e) {
      setFileContent(`// Error: ${e.message}`);
    } finally {
      setLoadingFile(false);
    }
  };

  const handleRefresh = () => setIframeKey(k => k + 1);

  // Open modal → preselect Account + Repo from the project's `full_name`
  // (owner/repo) so the founder sees EXACTLY where the project was first
  // imported from, with the option to switch to another connected account
  // or repo. This matches the Lovable/Emergent UX.
  const openPushModal = async () => {
    setPushOpen(true);
    setPushResult(null);
    const initialRepo = proj?.full_name || null;       // "owner/name"
    const initialOwner = initialRepo ? initialRepo.split('/')[0] : null;
    setSelectedRepo(initialRepo);
    setSelectedBranch('main');
    setNewBranchOpen(false);
    setNewBranchName('');
    if (ghRepos.length === 0) {
      setGhReposLoading(true);
      try {
        const r = await fetch(`${API}/api/github/repos`, { credentials: 'include' });
        if (r.ok) {
          const list = await r.json();
          setGhRepos(list);
          // Account = OWNER of the imported repo (not the first one returned
          // by GitHub — that previously caused the wrong account to show).
          const match = list.find(rp => rp.owner_login === initialOwner) || list[0];
          if (match) {
            setGhAccount({ login: match.owner_login, avatar_url: match.owner_avatar_url });
          } else if (initialOwner) {
            // Fallback: still show the owner from the project record even
            // if it's not in the install list (private org case).
            setGhAccount({ login: initialOwner, avatar_url: null });
          }
        }
      } catch { /* silent — modal still works with the tracked repo */ }
      finally { setGhReposLoading(false); }
    } else if (initialOwner) {
      const match = ghRepos.find(rp => rp.owner_login === initialOwner);
      if (match) setGhAccount({ login: match.owner_login, avatar_url: match.owner_avatar_url });
    }
  };

  // Whenever the founder picks a different repo, fetch its branches so we
  // pre-select the default and let them override.
  useEffect(() => {
    if (!pushOpen || !selectedRepo) return;
    let cancelled = false;
    (async () => {
      setBranchesLoading(true);
      try {
        const r = await fetch(
          `${API}/api/github/branches?repo=${encodeURIComponent(selectedRepo)}`,
          { credentials: 'include' }
        );
        if (r.ok && !cancelled) {
          const d = await r.json();
          setGhBranches(d.branches || []);
          setSelectedBranch(d.default_branch || 'main');
        }
      } catch { /* keep prior list */ }
      finally { if (!cancelled) setBranchesLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [pushOpen, selectedRepo]);

  // Create a new branch on the chosen repo via GitHub Refs API.
  // The dropdown row at the bottom of the Branch picker calls this.
  const handleCreateBranch = async () => {
    const name = newBranchName.trim();
    if (!name || creatingBranch || !selectedRepo) return;
    setCreatingBranch(true);
    try {
      const r = await fetch(`${API}/api/github/branches`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          repo: selectedRepo,
          name,
          from_branch: selectedBranch || 'main',
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setGhBranches(prev => [{ name, default: false }, ...prev.filter(b => b.name !== name)]);
        setSelectedBranch(name);
        setNewBranchOpen(false);
        setNewBranchName('');
        setBranchOpen(false);
      } else {
        // Surface error via the modal result UI so the founder sees what failed
        setPushResult({ error: d.detail || `Branch creation failed (HTTP ${r.status})` });
      }
    } catch (e) {
      setPushResult({ error: e?.message || String(e) });
    } finally {
      setCreatingBranch(false);
    }
  };

  const handlePush = async () => {
    if (pushing || !selectedRepo) return;
    setPushing(true);
    setPushResult(null);
    try {
      const r = await fetch(`${API}/api/import/${proj.project_id}/push`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          repo_full_name: selectedRepo,
          branch: selectedBranch || 'main',
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setPushResult({ error: d.detail || d.error || `HTTP ${r.status}` });
      } else {
        setPushResult(d);
      }
    } catch (e) {
      setPushResult({ error: e?.message || String(e) });
    } finally {
      setPushing(false);
    }
  };
  const closePushModal = () => {
    if (pushing) return;
    setPushOpen(false);
    setPushResult(null);
    setRepoOpen(false);
    setBranchOpen(false);
    setAccountOpen(false);
    setNewBranchOpen(false);
    setNewBranchName('');
  };

  const status = STATUS_LABEL[proj?.status] || { label: proj?.status || 'unknown', color: '#666', pulse: false };
  const previewUrl = `${API}/api/import/${proj.project_id}/serve?t=${iframeKey}`;

  return (
    <div data-testid="imported-project-view" style={{
      display: 'flex', flexDirection: 'column',
      height: '100%', width: '100%', minWidth: 0,
      background: '#0A0A0F', color: '#fff', overflow: 'hidden',
    }}>
      {/* Top chrome */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        height: 44,
        padding: '0 18px',
        background: '#0F0F15',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
      }}>
        {/* Project identity */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '0 1 auto' }}>
          <div style={{
            width: 28, height: 28, borderRadius: 6,
            background: githubConnected === false ? 'rgba(239,68,68,0.12)' : 'rgba(124,58,237,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            transition: 'background 0.3s',
          }}>
            <GithubIcon size={14} color={githubConnected === false ? '#EF4444' : '#7C3AED'} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {proj?.full_name || proj?.name}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: githubConnected === false ? '#EF4444' : status.color,
                animation: status.pulse ? 'pulse-glow 1.5s ease-in-out infinite' : 'none',
              }} />
              <span>{githubConnected === false ? 'Disconnected' : status.label}</span>
              {proj?.framework && <span>· {proj.framework}</span>}
              {proj?.type && !proj?.framework && <span>· {proj.type}</span>}
            </div>
          </div>
        </div>

        {/* URL bar (mock) */}
        <div style={{
          flex: 1, minWidth: 0,
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 14px',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.06)',
          fontSize: 11, color: 'rgba(255,255,255,0.5)',
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          <span style={{ color: '#7C3AED' }}>●</span>
          <span style={{ opacity: 0.5 }}>localhost/preview/</span>
          <span style={{ color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {proj?.name}
          </span>
        </div>

        {/* View toggle */}
        <div style={{
          display: 'flex', gap: 0,
          border: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.02)',
        }}>
          <button
            onClick={() => setViewMode('preview')}
            data-testid="preview-mode-btn"
            style={btnStyle(viewMode === 'preview')}
          >
            <Eye size={13} /> Preview
          </button>
          <button
            onClick={() => setViewMode('code')}
            data-testid="code-mode-btn"
            style={btnStyle(viewMode === 'code')}
          >
            <Code2 size={13} /> Code
          </button>
          <button
            onClick={() => setViewMode('dashboard')}
            data-testid="dashboard-mode-btn"
            style={btnStyle(viewMode === 'dashboard')}
          >
            <LayoutDashboard size={13} /> Dashboard
          </button>
        </div>

        {/* Actions */}
        <button onClick={handleRefresh} title="Refresh" style={iconBtn} data-testid="refresh-iframe-btn">
          <RefreshCw size={14} />
        </button>
        {githubConnected && proj?.full_name && (
          <button
            onClick={openPushModal}
            title="Save to GitHub · push current state"
            data-testid="save-to-github-btn"
            style={{
              ...iconBtn,
              padding: '6px 10px',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em',
              background: 'linear-gradient(135deg, rgba(124,58,237,0.18), rgba(255,107,26,0.16))',
              border: '1px solid rgba(124,58,237,0.45)',
              color: '#FFFFFF',
            }}
          >
            <GithubIcon size={13} />
            <span>Save to GitHub</span>
          </button>
        )}
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Open in new tab"
          style={{ ...iconBtn, textDecoration: 'none' }}
          data-testid="open-external-btn"
        >
          <ExternalLink size={14} />
        </a>
        {onClose && (
          <button onClick={onClose} title="Close project" style={iconBtn} data-testid="close-project-btn">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0, position: 'relative' }}>
        {/* GitHub disconnect banner (non-blocking) — modern glass strip */}
        {githubConnected === false && (
          <div style={{
            position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)',
            zIndex: 10, maxWidth: 560, minWidth: 420,
            padding: '12px 14px 12px 16px',
            background: 'rgba(15,15,21,0.92)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(239,68,68,0.25)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(239,68,68,0.08) inset',
            display: 'flex', alignItems: 'center', gap: 12,
            animation: 'fadeIn 0.4s ease-out',
          }} data-testid="gh-disconnected-banner">
            <div style={{
              width: 30, height: 30, borderRadius: '50%',
              background: 'rgba(239,68,68,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <GithubIcon size={14} color="#EF4444" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
                GitHub disconnected
              </div>
              <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.5)', marginTop: 2, lineHeight: 1.4 }}>
                You can still preview this project. Reconnect to sync updates & import more repos.
              </div>
            </div>
            <button
              onClick={handleReconnect}
              disabled={reconnecting}
              data-testid="gh-reconnect-btn"
              style={{
                padding: '8px 14px', background: '#7C3AED', color: '#000',
                border: 'none', fontSize: 11, fontWeight: 700, cursor: reconnecting ? 'wait' : 'pointer',
                fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
                flexShrink: 0, whiteSpace: 'nowrap',
                opacity: reconnecting ? 0.6 : 1,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { if (!reconnecting) e.currentTarget.style.boxShadow = '0 0 24px rgba(124,58,237,0.4)'; }}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              {reconnecting
                ? <><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> Connecting...</>
                : <><GithubIcon size={12} color="#000" /> Reconnect</>
              }
            </button>
          </div>
        )}

        {viewMode === 'preview' && (
          <div style={{ flex: 1, position: 'relative', background: '#fff' }}>
            {/* Modern build overlay — shown while installing/building */}
            {['cloned', 'installing', 'building'].includes(proj?.status) && (
              <BuildOverlay status={proj?.status} framework={proj?.framework} name={proj?.name} />
            )}

            {proj?.status === 'build_failed' && (
              <div style={{
                position: 'absolute', top: 16, left: 16, right: 16, zIndex: 2,
                padding: '12px 16px', background: 'rgba(239,68,68,0.95)', color: '#fff',
                fontSize: 12, display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <AlertCircle size={14} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>Build failed</div>
                  {proj.build_error && (
                    <div style={{ opacity: 0.9, fontSize: 10, marginTop: 4, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                      {proj.build_error.slice(0, 300)}
                    </div>
                  )}
                </div>
              </div>
            )}
            <iframe
              key={iframeKey}
              src={previewUrl}
              title="Imported project preview"
              data-testid="imported-iframe"
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          </div>
        )}

        {viewMode === 'code' && (
          <>
            {/* File tree */}
            <div style={{
              width: 260, flexShrink: 0, overflowY: 'auto',
              background: '#0F0F15', borderRight: '1px solid rgba(255,255,255,0.06)',
              padding: '10px 0',
            }}>
              <div style={{ padding: '0 14px 10px', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Files
              </div>
              <FileTree files={files} onOpen={loadFileContent} openFile={openFile} />
            </div>

            {/* Code pane */}
            <div style={{ flex: 1, overflow: 'auto', background: '#0A0A0F' }}>
              {!openFile && (
                <div style={{ padding: 60, textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>
                  <FileIcon size={32} style={{ margin: '0 auto 12px', display: 'block', opacity: 0.4 }} />
                  Select a file to view its content
                </div>
              )}
              {openFile && loadingFile && (
                <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              )}
              {openFile && !loadingFile && (
                <div>
                  <div style={{
                    padding: '8px 16px', background: 'rgba(124,58,237,0.05)',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
                    color: '#7C3AED',
                  }}>
                    {openFile}
                  </div>
                  <pre style={{
                    margin: 0, padding: 16, fontSize: 12, lineHeight: 1.6,
                    color: '#e6edf3', fontFamily: "'JetBrains Mono', monospace",
                    whiteSpace: 'pre', overflow: 'auto',
                  }}>
                    {fileContent}
                  </pre>
                </div>
              )}
            </div>
          </>
        )}

        {viewMode === 'dashboard' && (
          <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
            {/* For imported projects, the dashboard is owned by the PROJECT
                (stable id) — not by the active chat which changes every time
                the user types a new question. This keeps the session cache
                hot across conversations and prevents pointless re-generation. */}
            <DashboardShell chatId={proj?.project_id} />
          </div>
        )}
      </div>

      {/* Save to GitHub modal — Emergent-style picker (Account · Repo · Branch) */}
      {pushOpen && (
        <div
          data-testid="push-modal-overlay"
          onClick={closePushModal}
          style={{
            position: 'fixed', inset: 0, zIndex: 70,
            background: 'rgba(10, 10, 15, 0.78)',
            backdropFilter: 'blur(14px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, animation: 'fadeIn 0.18s ease',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            data-testid="push-modal"
            style={{
              width: '100%', maxWidth: 520,
              background: 'linear-gradient(180deg, #16161E 0%, #0F0F17 100%)',
              border: '1px solid rgba(124, 58, 237, 0.35)',
              borderRadius: 16,
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255,255,255,0.04) inset',
              padding: 22,
              color: '#FFFFFF',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 9,
                background: 'linear-gradient(135deg, #7C3AED 0%, #FF6B1A 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 6px 18px rgba(124,58,237,0.35)',
              }}>
                <GithubIcon size={15} color="#FFFFFF" />
              </div>
              <div style={{ flex: 1, fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>
                Save to GitHub
              </div>
              <button
                onClick={closePushModal}
                disabled={pushing}
                data-testid="push-modal-close"
                style={{
                  width: 28, height: 28, borderRadius: 7,
                  background: 'rgba(255,255,255,0.06)', border: 'none',
                  color: 'rgba(255,255,255,0.55)',
                  cursor: pushing ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <X size={14} />
              </button>
            </div>

            {/* Body — picker fields (hidden when we have a result) */}
            {!pushResult && (
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Account — clickable when the user has access to more than one owner */}
                <PickerField
                  label="Account"
                  icon={<GithubIcon size={12} color="rgba(255,255,255,0.55)" />}
                  value={(
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      {ghAccount?.avatar_url ? (
                        <img src={ghAccount.avatar_url} alt="" style={{
                          width: 20, height: 20, borderRadius: '50%',
                          border: '1px solid rgba(255,255,255,0.1)',
                        }} />
                      ) : (
                        <div style={{
                          width: 20, height: 20, borderRadius: '50%',
                          background: 'rgba(255,255,255,0.08)',
                        }} />
                      )}
                      <span style={{ fontWeight: 700 }}>{ghAccount?.login || '—'}</span>
                    </div>
                  )}
                  right={(
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '3px 10px', borderRadius: 999,
                        background: 'rgba(34, 197, 94, 0.14)',
                        border: '1px solid rgba(34, 197, 94, 0.32)',
                        fontSize: 10.5, fontWeight: 700, color: '#22C55E',
                        letterSpacing: '0.04em',
                      }}>
                        <span style={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: '#22C55E', boxShadow: '0 0 6px #22C55E',
                        }} />
                        Connected
                      </span>
                      <ChevronDown size={14} color="rgba(255,255,255,0.45)" />
                    </div>
                  )}
                  onClick={() => setAccountOpen(v => !v)}
                  open={accountOpen}
                  testId="picker-account"
                >
                  {accountOpen && (() => {
                    const owners = Array.from(new Map(ghRepos.map(r =>
                      [r.owner_login, { login: r.owner_login, avatar_url: r.owner_avatar_url }]
                    )).values());
                    return (
                      <Dropdown
                        items={owners.map(o => ({
                          key: o.login,
                          label: o.login,
                          meta: '',
                          selected: o.login === ghAccount?.login,
                        }))}
                        empty="No connected accounts"
                        onPick={(login) => {
                          const meta = owners.find(o => o.login === login);
                          setGhAccount(meta);
                          // When switching account, reset the repo to the
                          // first one owned by that account.
                          const firstRepo = ghRepos.find(r => r.owner_login === login);
                          setSelectedRepo(firstRepo ? firstRepo.full_name : null);
                          setAccountOpen(false);
                        }}
                      />
                    );
                  })()}
                </PickerField>

                {/* Repo selector — filtered to repos under the chosen Account */}
                <PickerField
                  label="Select Repo"
                  icon={<BookMarked size={12} color="rgba(255,255,255,0.55)" />}
                  value={(
                    <span style={{ fontWeight: 700 }}>
                      {selectedRepo ? selectedRepo.split('/').pop() : (ghReposLoading ? 'Loading…' : 'Pick a repo')}
                    </span>
                  )}
                  right={<ChevronDown size={14} color="rgba(255,255,255,0.45)" />}
                  onClick={() => setRepoOpen(v => !v)}
                  open={repoOpen}
                  testId="picker-repo"
                >
                  {repoOpen && (
                    <Dropdown
                      items={ghRepos
                        .filter(r => !ghAccount?.login || r.owner_login === ghAccount.login)
                        .map(r => ({
                          key: r.full_name,
                          label: r.name,
                          meta: r.private ? 'private' : '',
                          selected: r.full_name === selectedRepo,
                        }))}
                      empty={ghReposLoading ? 'Loading repos…' : `No repos under ${ghAccount?.login || 'this account'}`}
                      onPick={key => { setSelectedRepo(key); setRepoOpen(false); }}
                    />
                  )}
                </PickerField>

                {/* Branch selector — includes inline "+ Create New Branch" */}
                <PickerField
                  label="Select Branch"
                  icon={<GitBranch size={12} color="rgba(255,255,255,0.55)" />}
                  value={(
                    <span style={{ fontWeight: 700 }}>
                      {branchesLoading ? 'Loading…' : (selectedBranch || 'main')}
                    </span>
                  )}
                  right={<ChevronDown size={14} color="rgba(255,255,255,0.45)" />}
                  onClick={() => setBranchOpen(v => !v)}
                  open={branchOpen}
                  testId="picker-branch"
                >
                  {branchOpen && (
                    <div
                      onClick={e => e.stopPropagation()}
                      style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6,
                        background: 'linear-gradient(180deg, #1A1A24 0%, #131321 100%)',
                        border: '1px solid rgba(124, 58, 237, 0.35)',
                        borderRadius: 12,
                        boxShadow: '0 18px 44px rgba(0,0,0,0.5)',
                        zIndex: 5, overflow: 'hidden',
                        maxHeight: 280, overflowY: 'auto',
                      }}
                    >
                      {ghBranches.length === 0 && !branchesLoading && (
                        <div style={{
                          padding: '12px 13px', fontSize: 12,
                          color: 'rgba(255,255,255,0.5)', fontStyle: 'italic',
                        }}>No branches yet</div>
                      )}
                      {ghBranches.map(b => (
                        <div
                          key={b.name}
                          onClick={() => { setSelectedBranch(b.name); setBranchOpen(false); }}
                          data-testid={`branch-item-${b.name}`}
                          style={{
                            padding: '10px 13px',
                            display: 'flex', alignItems: 'center', gap: 8,
                            cursor: 'pointer',
                            background: b.name === selectedBranch ? 'rgba(34,197,94,0.14)' : 'transparent',
                            borderLeft: b.name === selectedBranch ? '2px solid #22C55E' : '2px solid transparent',
                          }}
                          onMouseEnter={e => {
                            if (b.name !== selectedBranch) e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                          }}
                          onMouseLeave={e => {
                            if (b.name !== selectedBranch) e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <div style={{
                            flex: 1, fontSize: 12.5, fontWeight: 600,
                            color: b.name === selectedBranch ? '#22C55E' : '#FFFFFF',
                          }}>{b.name}</div>
                          {b.name === selectedBranch && <CheckCircle2 size={13} color="#22C55E" />}
                          {b.default && b.name !== selectedBranch && (
                            <span style={{
                              fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em',
                              color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase',
                            }}>default</span>
                          )}
                        </div>
                      ))}

                      {/* Create New Branch */}
                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                        {!newBranchOpen ? (
                          <div
                            onClick={() => setNewBranchOpen(true)}
                            data-testid="branch-create-new-btn"
                            style={{
                              padding: '10px 13px',
                              display: 'flex', alignItems: 'center', gap: 8,
                              cursor: 'pointer',
                              color: '#22C55E', fontWeight: 700, fontSize: 12.5,
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(34,197,94,0.08)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontSize: 14, lineHeight: 1 }}>+</span>
                            Create New Branch
                          </div>
                        ) : (
                          <div style={{ padding: '10px 13px', display: 'flex', gap: 6 }}>
                            <input
                              autoFocus
                              data-testid="branch-create-new-input"
                              value={newBranchName}
                              onChange={e => setNewBranchName(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleCreateBranch();
                                if (e.key === 'Escape') { setNewBranchOpen(false); setNewBranchName(''); }
                              }}
                              placeholder="branch-name"
                              style={{
                                flex: 1,
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 7,
                                padding: '7px 10px',
                                color: '#FFFFFF', fontFamily: 'inherit',
                                fontSize: 12, outline: 'none',
                              }}
                            />
                            <button
                              onClick={handleCreateBranch}
                              disabled={creatingBranch || !newBranchName.trim()}
                              data-testid="branch-create-confirm"
                              style={{
                                padding: '0 12px', borderRadius: 7,
                                background: newBranchName.trim() && !creatingBranch
                                  ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)'
                                  : 'rgba(255,255,255,0.08)',
                                border: 'none', color: '#FFFFFF',
                                fontFamily: 'inherit', fontWeight: 700, fontSize: 11,
                                cursor: newBranchName.trim() && !creatingBranch ? 'pointer' : 'not-allowed',
                                display: 'inline-flex', alignItems: 'center',
                              }}
                            >
                              {creatingBranch
                                ? <Loader2 size={11} style={{ animation: 'spin 0.7s linear infinite' }} />
                                : 'Create'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </PickerField>

                {/* Action row */}
                <div style={{ display: 'flex', gap: 9, marginTop: 6, justifyContent: 'flex-end' }}>
                  <button
                    onClick={closePushModal}
                    disabled={pushing}
                    data-testid="push-modal-cancel"
                    style={{
                      padding: '10px 18px', borderRadius: 999,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'rgba(255,255,255,0.75)',
                      fontFamily: 'inherit', fontWeight: 600, fontSize: 12.5,
                      cursor: pushing ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePush}
                    disabled={pushing || !selectedRepo}
                    data-testid="push-modal-submit"
                    style={{
                      padding: '10px 18px', borderRadius: 999,
                      background: (selectedRepo && !pushing)
                        ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)'
                        : 'rgba(34,197,94,0.25)',
                      border: 'none',
                      color: '#FFFFFF',
                      fontFamily: 'inherit', fontWeight: 700, fontSize: 12.5,
                      letterSpacing: '0.02em',
                      cursor: (selectedRepo && !pushing) ? 'pointer' : 'not-allowed',
                      display: 'inline-flex', alignItems: 'center', gap: 7,
                      boxShadow: (selectedRepo && !pushing) ? '0 8px 22px rgba(34,197,94,0.32)' : 'none',
                    }}
                  >
                    {pushing ? (
                      <>
                        <Loader2 size={13} style={{ animation: 'spin 0.7s linear infinite' }} />
                        Saving…
                      </>
                    ) : (
                      <>
                        Save to GitHub
                        <UploadCloud size={14} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Success */}
            {pushResult && pushResult.ok && (
              <div data-testid="push-result-success" style={{ marginTop: 16 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                  padding: '13px 14px',
                  background: 'rgba(34, 197, 94, 0.12)',
                  border: '1px solid rgba(34, 197, 94, 0.4)',
                  borderRadius: 11,
                }}>
                  <CheckCircle2 size={18} color="#22C55E" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>
                      {pushResult.no_op ? 'Nothing to push' : 'Saved to GitHub'}
                    </div>
                    {!pushResult.no_op && (
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
                        {pushResult.repo_full_name} · <strong style={{ color: '#FFFFFF' }}>{pushResult.branch}</strong>
                        {' · '}
                        <code style={{
                          padding: '0 6px', borderRadius: 4,
                          background: 'rgba(255,255,255,0.08)', fontSize: 10.5,
                        }}>{pushResult.commit_sha}</code>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 9, justifyContent: 'flex-end' }}>
                  {pushResult.repo_url && !pushResult.no_op && (
                    <a
                      href={pushResult.repo_url}
                      target="_blank" rel="noopener noreferrer"
                      data-testid="push-result-view-commit"
                      style={{
                        padding: '9px 16px', borderRadius: 999,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        color: 'rgba(255,255,255,0.85)',
                        textDecoration: 'none',
                        fontSize: 12, fontWeight: 600,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      <ExternalLink size={12} />
                      View commit
                    </a>
                  )}
                  <button
                    onClick={closePushModal}
                    data-testid="push-result-close"
                    style={{
                      padding: '9px 18px', borderRadius: 999,
                      background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                      border: 'none', color: '#FFFFFF',
                      fontFamily: 'inherit', fontWeight: 700, fontSize: 12,
                      cursor: 'pointer',
                      boxShadow: '0 8px 22px rgba(34,197,94,0.32)',
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}

            {/* Error */}
            {pushResult && pushResult.error && (
              <div data-testid="push-result-error" style={{ marginTop: 16 }}>
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '13px 14px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: 11,
                }}>
                  <AlertCircle size={18} color="#EF4444" style={{ marginTop: 1, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                      Push failed
                    </div>
                    <div style={{
                      fontSize: 11.5, color: 'rgba(255,255,255,0.7)',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {pushResult.error}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 9, marginTop: 12, justifyContent: 'flex-end' }}>
                  <button
                    onClick={closePushModal}
                    style={{
                      padding: '9px 18px', borderRadius: 999,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'rgba(255,255,255,0.75)',
                      fontFamily: 'inherit', fontWeight: 600, fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => setPushResult(null)}
                    data-testid="push-result-retry"
                    style={{
                      padding: '9px 18px', borderRadius: 999,
                      background: 'linear-gradient(135deg, #7C3AED 0%, #FF6B1A 100%)',
                      border: 'none', color: '#FFFFFF',
                      fontFamily: 'inherit', fontWeight: 700, fontSize: 12,
                      cursor: 'pointer',
                      boxShadow: '0 8px 22px rgba(124,58,237,0.32)',
                    }}
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FileTree({ files, onOpen, openFile, depth = 0 }) {
  return (
    <div>
      {files.map((f) => (
        <FileNode key={f.path} node={f} onOpen={onOpen} openFile={openFile} depth={depth} />
      ))}
    </div>
  );
}

function FileNode({ node, onOpen, openFile, depth }) {
  const [expanded, setExpanded] = useState(depth < 1);
  const isDir = node.type === 'dir';
  const isOpen = openFile === node.path;

  return (
    <div>
      <button
        onClick={() => isDir ? setExpanded(e => !e) : onOpen(node.path)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          width: '100%', padding: '5px 14px', paddingLeft: 14 + depth * 14,
          background: isOpen ? 'rgba(124,58,237,0.08)' : 'transparent',
          border: 'none', textAlign: 'left',
          color: isOpen ? '#7C3AED' : 'rgba(255,255,255,0.7)',
          fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
        }}
        onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
        onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'transparent'; }}
      >
        {isDir
          ? (expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />)
          : <span style={{ width: 11 }} />}
        {isDir
          ? <Folder size={12} color="rgba(255,255,255,0.4)" />
          : <FileIcon size={12} color="rgba(255,255,255,0.4)" />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.name}</span>
      </button>
      {isDir && expanded && node.children && (
        <FileTree files={node.children} onOpen={onOpen} openFile={openFile} depth={depth + 1} />
      )}
    </div>
  );
}

const btnStyle = (active) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '7px 12px',
  background: active ? 'rgba(124,58,237,0.12)' : 'transparent',
  color: active ? '#7C3AED' : 'rgba(255,255,255,0.5)',
  border: 'none', fontSize: 11, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
});

const iconBtn = {
  width: 32, height: 32,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.06)',
  color: 'rgba(255,255,255,0.6)',
  cursor: 'pointer', transition: 'all 0.15s',
};


/* ---------- Modern build overlay (Vercel/Netlify style) ---------- */
function BuildOverlay({ status, framework, name }) {
  const steps = [
    { key: 'cloned', label: 'Cloning repository' },
    { key: 'installing', label: 'Installing dependencies' },
    { key: 'building', label: 'Building project' },
    { key: 'ready', label: 'Deploying preview' },
  ];
  const order = ['cloned', 'installing', 'building', 'ready'];
  const currentIdx = Math.max(0, order.indexOf(status));

  // Elapsed timer
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <div
      data-testid="build-overlay"
      style={{
        position: 'absolute', inset: 0, zIndex: 5,
        background: 'radial-gradient(ellipse at center, #0F0F15 0%, #06060A 100%)',
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'JetBrains Mono', monospace",
        overflow: 'hidden',
      }}
    >
      {/* Ambient animated background grid */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `
          linear-gradient(rgba(124,58,237,0.04) 1px, transparent 1px),
          linear-gradient(90deg, rgba(124,58,237,0.04) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        animation: 'grid-drift 20s linear infinite',
        opacity: 0.5,
      }} />

      {/* Glow orb */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        width: 500, height: 500, marginLeft: -250, marginTop: -250,
        background: 'radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 70%)',
        animation: 'pulse-glow 3s ease-in-out infinite',
        pointerEvents: 'none',
      }} />

      {/* Center card */}
      <div style={{
        position: 'relative', zIndex: 2,
        maxWidth: 480, width: '90%',
        padding: '40px 44px',
        background: 'rgba(20,20,28,0.8)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(124,58,237,0.15)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(124,58,237,0.05) inset',
      }}>
        {/* Top: animated logo + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
          <div style={{
            width: 44, height: 44, position: 'relative',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              position: 'absolute', inset: 0,
              border: '2px solid rgba(124,58,237,0.2)',
              borderTopColor: '#7C3AED',
              borderRadius: '50%',
              animation: 'spin 1.2s linear infinite',
            }} />
            <div style={{
              width: 8, height: 8, borderRadius: '50%', background: '#7C3AED',
              boxShadow: '0 0 16px #7C3AED',
              animation: 'pulse-dot 1.5s ease-in-out infinite',
            }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#7C3AED', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Live Build
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {name}
            </div>
          </div>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)',
            padding: '6px 10px', background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            {mm}:{ss}
          </div>
        </div>

        {/* Steps checklist */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 24 }}>
          {steps.map((s, i) => {
            const isDone = i < currentIdx;
            const isActive = i === currentIdx;
            return (
              <div key={s.key} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                opacity: i > currentIdx ? 0.35 : 1,
                transition: 'opacity 0.4s',
              }}>
                <div style={{
                  width: 22, height: 22, flexShrink: 0,
                  borderRadius: '50%',
                  background: isDone ? '#7C3AED' : isActive ? 'rgba(124,58,237,0.15)' : 'rgba(255,255,255,0.05)',
                  border: isActive ? '2px solid #7C3AED' : '1px solid rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative',
                }}>
                  {isDone && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                  {isActive && (
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: '#7C3AED',
                      animation: 'pulse-dot 1s ease-in-out infinite',
                    }} />
                  )}
                </div>
                <div style={{ flex: 1, fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? '#fff' : 'rgba(255,255,255,0.7)' }}>
                  {s.label}
                </div>
                {isActive && (
                  <div style={{ fontSize: 10, color: '#7C3AED', fontWeight: 600, letterSpacing: '0.05em' }}>
                    IN PROGRESS
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Indeterminate progress bar */}
        <div style={{
          height: 3, background: 'rgba(255,255,255,0.06)',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, bottom: 0,
            width: '30%',
            background: 'linear-gradient(90deg, transparent, #7C3AED, transparent)',
            animation: 'slide-progress 1.6s ease-in-out infinite',
          }} />
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 20, fontSize: 11, color: 'rgba(255,255,255,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>{framework ? framework.toUpperCase() : 'STATIC'}</span>
          <span>Auto-built from source · Never stale</span>
        </div>
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes grid-drift { from { background-position: 0 0; } to { background-position: 40px 40px; } }
        @keyframes pulse-dot { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.8); } }
        @keyframes slide-progress { 0% { left: -30%; } 100% { left: 100%; } }
      `}</style>
    </div>
  );
}


/* ─── Save-to-GitHub modal · reusable primitives ──────────────────────────
   Tiny presentational components used by the Account / Repo / Branch picker.
   They live here (not in a shared ui folder) because the picker only
   renders inside this one modal and we keep one-shot abstractions local. */

function PickerField({ label, icon, value, right, onClick, open, disabled, children, testId }) {
  return (
    <div data-testid={testId}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7,
        fontSize: 10.5, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.55)', marginBottom: 8,
      }}>
        {icon}
        <span>{label}</span>
      </div>
      <div
        onClick={disabled ? undefined : onClick}
        style={{
          position: 'relative',
          padding: '11px 14px',
          background: open ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.035)',
          border: `1px solid ${open ? 'rgba(124,58,237,0.55)' : 'rgba(255,255,255,0.09)'}`,
          borderRadius: 11,
          fontSize: 13, color: '#FFFFFF',
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 10,
          transition: 'background 0.15s, border-color 0.15s',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value}
        </div>
        {right}
        {children}
      </div>
    </div>
  );
}

function Dropdown({ items, empty, onPick }) {
  return (
    <div
      data-testid="picker-dropdown"
      onClick={e => e.stopPropagation()}
      style={{
        position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6,
        background: 'linear-gradient(180deg, #1A1A24 0%, #131321 100%)',
        border: '1px solid rgba(124, 58, 237, 0.35)',
        borderRadius: 12,
        boxShadow: '0 18px 44px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset',
        zIndex: 5, overflow: 'hidden',
        maxHeight: 240, overflowY: 'auto',
      }}
    >
      {items.length === 0 ? (
        <div style={{
          padding: '14px 14px', fontSize: 12,
          color: 'rgba(255,255,255,0.5)', fontStyle: 'italic',
        }}>
          {empty}
        </div>
      ) : items.map(it => (
        <div
          key={it.key}
          onClick={() => onPick(it.key)}
          data-testid={`dropdown-item-${it.key}`}
          style={{
            padding: '10px 13px',
            display: 'flex', alignItems: 'center', gap: 9,
            cursor: 'pointer',
            background: it.selected ? 'rgba(124,58,237,0.18)' : 'transparent',
            borderLeft: it.selected ? '2px solid #7C3AED' : '2px solid transparent',
            transition: 'background 0.1s',
          }}
          onMouseEnter={e => { if (!it.selected) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          onMouseLeave={e => { if (!it.selected) e.currentTarget.style.background = 'transparent'; }}
        >
          <div style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: '#FFFFFF' }}>{it.label}</div>
          {it.meta && (
            <div style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em',
              color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase',
            }}>{it.meta}</div>
          )}
        </div>
      ))}
    </div>
  );
}
